// frontend/src/pages/StaffDirectory.js
import React, { useState, useMemo, useEffect, Suspense } from 'react';
import { 
  Users, 
  Building2, 
  Phone,
  Hash,
  Mail, 
  User,
  X,
  Briefcase,
  UserCheck,
  Grid3X3,
  List,
  Network,
  Download
} from 'lucide-react';
import { useStaffDirectoryPage } from '../hooks/useStaffDirectoryData';
import { useTabPersistence } from '../hooks/usePersistence';
import { formatDate } from '../utils/dateUtils';
import { getSafeWorkEmail } from '../utils/emailUtils';
import { formatDirectLine, formatPhoneForDisplay, phoneToTelHref, splitWorkContact, toLocalSgDigits } from '../utils/phoneUtils';
import Modal from '../components/UI/Modal';
import { DetailField } from '../components/UI/DetailFieldGrid';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { getDepartmentIcon, getDepartmentColor, getDepartmentHex } from '../config/departmentConfig';
import { TradeBadge } from '../components/UI/TradeBadge';
import { isTradeRedundantWithDepartment } from '../config/tradeConfig';
import RoleBadge from '../components/RoleBadge';
import { ClassificationBadge } from '../components/UI/ClassificationBadge';
import { getClassificationAvatarHex, getClassificationColor } from '../utils/classificationConfig';
import { Pagination } from '../components/UI/Pagination';
import { FilterButton } from '../components/UI/FilterButton';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import PillToggle from '../components/UI/PillToggle';
import toast from '../utils/toast';

const OrgChart = React.lazy(() => import('../components/OrgChart/OrgChart'));

const matchesWorkContact = (employee, rawTerm) => {
  const digits = String(rawTerm || '').replace(/\D/g, '');
  if (digits.length < 3) return false;
  const { mobile, did, extension } = splitWorkContact(employee);
  const haystack = `${toLocalSgDigits(mobile)}${toLocalSgDigits(did)}${String(extension || '').replace(/\D/g, '')}`;
  return haystack.includes(digits);
};

const PhoneFact = ({ phone, text }) => {
  const href = phoneToTelHref(phone);
  if (!href) return text;
  return (
    <a href={href} className="text-blue-600 hover:text-blue-700">
      {text}
    </a>
  );
};

const DirectoryPhoneLines = ({ employee, dense = false }) => {
  const { mobile, did, extension } = splitWorkContact(employee);
  if (!mobile && !did) return null;

  const iconClass = dense ? 'w-3 h-3 text-gray-400 flex-shrink-0' : 'w-4 h-4 text-gray-400 flex-shrink-0';
  const rowClass = `flex items-center min-w-0 text-sm text-gray-600 ${dense ? 'gap-2' : 'gap-3'}`;

  const Line = ({ icon: Icon, label, title, text }) => (
    <div className={rowClass} title={title}>
      <Icon className={iconClass} aria-hidden="true" />
      <span className="w-12 flex-shrink-0 text-xs font-medium text-gray-500">{label}</span>
      <span className="truncate">{text}</span>
    </div>
  );

  return (
    <>
      {mobile && (
        <Line
          icon={Phone}
          label="Work"
          title={`Work phone ${formatPhoneForDisplay(mobile)}`}
          text={formatPhoneForDisplay(mobile)}
        />
      )}
      {did && (
        <Line
          icon={Hash}
          label="Direct"
          title={`Direct line ${formatDirectLine(did, extension)}`}
          text={formatDirectLine(did, extension)}
        />
      )}
    </>
  );
};

const DirectoryContactDetails = ({ employee }) => {
  const contact = splitWorkContact(employee);
  const email = getSafeWorkEmail(employee);
  if (!email && !contact.mobile && !contact.did) {
    return <p className="text-sm text-gray-500">No work contact on file</p>;
  }

  return (
    <div className="space-y-4">
      {email && (
        <DetailField label="Work email">
          <a href={`mailto:${email}`} className="text-blue-600 hover:text-blue-700 break-all">
            {email}
          </a>
        </DetailField>
      )}
      {contact.mobile && (
        <DetailField label="Work phone">
          <PhoneFact phone={contact.mobile} text={formatPhoneForDisplay(contact.mobile)} />
        </DetailField>
      )}
      {contact.did && (
        <DetailField label="Direct line">
          <PhoneFact phone={contact.did} text={formatDirectLine(contact.did, contact.extension)} />
        </DetailField>
      )}
    </div>
  );
};

const normalizeDepartmentId = (departmentId) => (
  departmentId == null ? '' : String(departmentId)
);

const StaffDirectory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [viewMode, setViewMode] = useTabPersistence('staff-directory-view-mode', 'grid', ['grid', 'list', 'org']);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);

  // Organization chart classification group selection: 'all' | 'staff' | 'workers'
  const [selectedOrgGroup, setSelectedOrgGroup] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 24; // Optimal for directory browsing: 4-6 rows in grid view

  // React Query hook for data fetching
  const { 
    employees, 
    departments, 
    isLoading, 
    isError,
    refetch 
  } = useStaffDirectoryPage();

  // Helper: alias-first display name (Standardized Western format: First, Last)
  const getDisplayName = (emp) => (emp.alias && emp.alias.trim()) ? emp.alias.trim() : `${emp.first_name} ${emp.last_name}`.trim();
  
  // Simplified avatar logic - just first letter of first name (or alias)
  const getAvatarInitial = (emp) => {
    const displayName = getDisplayName(emp);
    return displayName ? displayName.charAt(0).toUpperCase() : '';
  };
  const getLegalName = (emp) => `${emp.first_name || ''} ${emp.last_name || ''}`.trim();

  // One department scope drives every view. This keeps the filter panel,
  // organization-chart tabs, empty states, and exports consistent.
  const selectedDepartmentName = useMemo(() => {
    if (!selectedDepartment) return 'All';
    const configuredName = departments.find(
      (department) => normalizeDepartmentId(department.id) === selectedDepartment
    )?.department_name;
    if (configuredName) return configuredName;

    return employees.find(
      (employee) => normalizeDepartmentId(employee.department?.id) === selectedDepartment
    )?.department?.department_name || 'Selected';
  }, [departments, employees, selectedDepartment]);

  // Filter and search logic
  const filteredEmployees = useMemo(() => {
    const filtered = employees.filter(employee => {
      const name = getDisplayName(employee);
      const term = searchTerm.toLowerCase();
      const safeWorkEmail = getSafeWorkEmail(employee); // Only search valid work emails
      const hodLabel = employee.is_hod ? 'hod' : '';
      const dyHodLabel = employee.is_deputy_hod ? 'dy hod deputy hod' : '';
      const matchesSearch = !searchTerm || 
        name.toLowerCase().includes(term) ||
        `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(term) ||
        (safeWorkEmail || '').toLowerCase().includes(term) ||
        (employee.designation || '').toLowerCase().includes(term) ||
        (employee.staff_id || '').toLowerCase().includes(term) ||
        hodLabel.includes(term) ||
        dyHodLabel.includes(term) ||
        matchesWorkContact(employee, searchTerm);
      
      // Compare UUID strings directly
      const matchesDepartment = !selectedDepartment || 
        normalizeDepartmentId(employee.department?.id) === selectedDepartment;
      
      return matchesSearch && matchesDepartment && !['inactive'].includes(employee.employment_status);
    });
    
    return filtered;
  }, [employees, searchTerm, selectedDepartment]);

  // Pagination logic - slice filtered employees for current page
  const totalPages = Math.ceil(filteredEmployees.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedEmployees = useMemo(() => {
    return filteredEmployees.slice(startIndex, endIndex);
  }, [filteredEmployees, startIndex, endIndex]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedDepartment]);

  const openEmployeeModal = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeModal(true);
  };

  const closeEmployeeModal = () => {
    setSelectedEmployee(null);
    setShowEmployeeModal(false);
  };

  // --- Premium PDF / Print export helpers ---

  // Escape any text injected into the print HTML to prevent malformed markup
  // or accidental injection from designation/name fields.
  const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  // Build the reporting-officer tree HTML for an arbitrary slice of employees.
  // Roots are anyone whose reporting officer is not in the slice (or is null).
  //
  // options.stackDepth (1-based, optional): the level whose members render as
  // a vertical stack of compact rows (name + designation + rolled-up report
  // count) instead of full cards with further branches — for "leaf-heavy"
  // levels with many siblings. e.g. stackDepth: 3 keeps a wide level 3 legible
  // on one landscape page while still surfacing level 4+ headcount per person.
  // Capped at STACK_ROW_CAP rows per manager with a "+N more" row beyond that.
  //
  // options.summaryDepth (1-based, optional): the level at which we stop drawing
  // individual person cards and instead roll everyone beneath into a single
  // consolidated count node. e.g. summaryDepth: 4 → full cards for levels 1 & 2,
  // stacked rows at level 3 (when stackDepth is set), and anyone deeper still
  // rolled into a "N team members" node. Omit both options to render the full
  // tree (unchanged behaviour).
  const buildOrgChartTreeHTML = (emps, options = {}) => {
    const { summaryDepth = null, stackDepth = null } = options;
    const empMap = new Map();
    const childrenMap = new Map();
    emps.forEach((emp) => {
      empMap.set(emp.staff_id, emp);
      childrenMap.set(emp.staff_id, []);
    });
    const roots = [];
    emps.forEach((emp) => {
      const roId = emp.reporting_officer?.staff_id;
      if (!roId || !empMap.has(roId)) roots.push(emp);
      else childrenMap.get(roId).push(emp);
    });

    // Total people anywhere beneath a node (all levels), cycle-safe.
    const countDescendants = (rootId) => {
      const seen = new Set([rootId]);
      const queue = [...(childrenMap.get(rootId) || [])];
      let count = 0;
      while (queue.length) {
        const n = queue.shift();
        if (seen.has(n.staff_id)) continue;
        seen.add(n.staff_id);
        count += 1;
        (childrenMap.get(n.staff_id) || []).forEach((k) => {
          if (!seen.has(k.staff_id)) queue.push(k);
        });
      }
      return count;
    };

    // Consolidated rollup shown in place of a manager's report cards.
    const renderSummaryNode = (total, direct) => {
      const label = total === 1 ? 'team member' : 'team members';
      const sub = total > direct
        ? `<div class="emp-summary-sub">${direct} direct</div>`
        : '';
      return `
        <div class="org-branch">
          <div class="emp-summary">
            <div class="emp-summary-count">${total}</div>
            <div class="emp-summary-label">${label}</div>
            ${sub}
          </div>
        </div>`;
    };

    const renderedIds = new Set();

    // Vertical stack of compact rows for a "leaf-heavy" level (e.g. level 3
    // under managers): each row is a single line (name + designation) plus a
    // rolled-up count of everyone beneath that person, instead of a full
    // card with its own further branches. Keeps a wide level legible on one
    // landscape page. Capped so page height stays bounded as the org grows.
    const STACK_ROW_CAP = 12;
    const renderStackNode = (kids) => {
      const shown = kids.slice(0, STACK_ROW_CAP);
      const overflow = kids.length - shown.length;
      const rows = shown.map((k) => {
        renderedIds.add(k.staff_id);
        const deptHex = getDepartmentHex(k.department?.department_name || '');
        const reportCount = countDescendants(k.staff_id);
        const countChip = reportCount > 0
          ? `<span class="emp-mini-count">${reportCount} report${reportCount === 1 ? '' : 's'}</span>`
          : '';
        return `
          <div class="emp-mini" style="border-left-color:${deptHex}">
            <div class="emp-mini-text">
              <div class="emp-mini-name">${escapeHtml(getDisplayName(k))}</div>
              <div class="emp-mini-designation">${escapeHtml(k.designation || 'Not specified')}</div>
            </div>
            ${countChip}
          </div>`;
      }).join('');
      const moreRow = overflow > 0
        ? `<div class="emp-mini emp-mini-more">+${overflow} more</div>`
        : '';
      return `
        <div class="org-branch">
          <div class="emp-stack">${rows}${moreRow}</div>
        </div>`;
    };

    const renderNode = (emp, depth) => {
      if (renderedIds.has(emp.staff_id)) return ''; // cycle guard
      renderedIds.add(emp.staff_id);

      const kids = childrenMap.get(emp.staff_id) || [];
      let childrenHTML = '';
      if (kids.length) {
        if (stackDepth && depth + 1 === stackDepth) {
          childrenHTML = `<div class="org-children org-children-single">${renderStackNode(kids)}</div>`;
        } else if (summaryDepth && depth + 1 >= summaryDepth) {
          const total = countDescendants(emp.staff_id);
          childrenHTML = `<div class="org-children org-children-single">${renderSummaryNode(total, kids.length)}</div>`;
        } else {
          const singleChildCls = kids.length === 1 ? ' org-children-single' : '';
          childrenHTML = `<div class="org-children${singleChildCls}">${kids.map((k) => renderNode(k, depth + 1)).join('')}</div>`;
        }
      }

      // Department strip + label colours come from departmentConfig (DS source of truth).
      const deptName = emp.department?.department_name || '';
      const deptHex = getDepartmentHex(deptName);
      const deptStrip = `<div class="emp-card-strip" style="background:${deptHex}"></div>`;
      const deptLine = deptName
        ? `<div class="emp-meta"><span class="emp-dept-name" style="color:${deptHex}">${escapeHtml(deptName)}</span></div>`
        : '<div class="emp-meta"></div>';
      const cardCls = `emp-card${emp.is_hod ? ' emp-card-hod' : ''}`;
      return `
        <div class="org-branch">
          <div class="${cardCls}">
            ${deptStrip}
            <div class="emp-name">${escapeHtml(getDisplayName(emp))}</div>
            <div class="emp-designation">${escapeHtml(emp.designation || 'Not specified')}</div>
            ${deptLine}
          </div>
          ${childrenHTML}
        </div>`;
    };

    return roots.map((r) => renderNode(r, 1)).join('');
  };

  // --- Direct PDF download via html2canvas + jsPDF ---
  // Uses the branded PDF template (pdfReportTemplate.js) for header/footer
  // and html2canvas to capture the tree as a high-res raster image.

  // CSS tuned specifically for html2canvas rendering.
  // Key rules:
  //   • No -webkit-line-clamp — html2canvas clips clamped text mid-line.
  //   • Inset box-shadows dropped (html2canvas renders them poorly).
  //   • PDF cards: name / designation / department only. Department colour
  //     identity comes from getDepartmentHex (departmentConfig / DS).
  const ORG_CHART_TREE_CSS = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    .org-tree {
      display: inline-flex; flex-wrap: nowrap; gap: 24px;
      align-items: flex-start; padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      -webkit-font-smoothing: antialiased;
    }
    .org-branch { display: flex; flex-direction: column; align-items: center; position: relative; }
    .org-children {
      position: relative; display: flex; justify-content: center; gap: 16px;
      margin-top: 22px; padding-top: 22px;
    }
    .org-children::before {
      content: ''; position: absolute; top: 0; left: 50%;
      width: 1px; height: 11px; background: #cbd5e1; transform: translateX(-0.5px);
    }
    .org-children::after {
      content: ''; position: absolute; top: 11px; left: 0; right: 0;
      height: 1px; background: #cbd5e1;
    }
    .org-children-single::after { display: none; }
    .org-children > .org-branch::before {
      content: ''; position: absolute; top: -11px; left: 50%;
      width: 1px; height: 11px; background: #cbd5e1; transform: translateX(-0.5px);
    }

    /* Card sized to fit common designation lengths on at most 2 lines. */
    .emp-card {
      position: relative; width: 200px; background: #ffffff;
      border: 1px solid #e5e7eb; border-radius: 10px;
      padding: 14px 12px 12px; box-shadow: 0 1px 2px rgba(15,23,42,0.04);
      text-align: center;
    }
    .emp-card-hod { border-color: #c4b5fd; box-shadow: 0 1px 3px rgba(124,58,237,0.12); }
    /* Department accent — hex from departmentConfig (DS). */
    .emp-card-strip {
      position: absolute; top: 0; left: 0; right: 0; height: 4px;
      border-top-left-radius: 10px; border-top-right-radius: 10px;
    }

    .emp-name {
      font-size: 12px; font-weight: 600; color: #0f172a;
      line-height: 1.3; word-break: break-word;
    }

    /* No line-clamp — let designation wrap to as many lines as it needs.
       Variable card heights are acceptable; clipped text is not. */
    .emp-designation {
      font-size: 10px; color: #475569;
      margin-top: 4px; line-height: 1.35;
      word-break: break-word;
    }

    .emp-meta {
      margin-top: 8px;
      text-align: center;
    }
    .emp-meta:empty { margin-top: 0; height: 0; }
    .emp-dept-name {
      font-size: 9.5px; font-weight: 600;
      letter-spacing: 0.1px; line-height: 14px;
    }

    /* Stacked rows (leaf-heavy level, e.g. level 3): one compact line per
       person instead of a full card. Left border keeps the same department
       accent language as .emp-card-strip; count chip uses the slate tokens
       already established by .emp-designation / .emp-summary-sub. */
    .emp-stack {
      width: 200px; display: flex; flex-direction: column; gap: 6px;
    }
    .emp-mini {
      display: flex; align-items: center; justify-content: space-between;
      gap: 8px; text-align: left;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-left: 3px solid #94a3b8;
      border-radius: 8px;
      padding: 6px 10px;
    }
    .emp-mini-text { min-width: 0; flex: 1 1 auto; }
    .emp-mini-name {
      font-size: 9.5px; font-weight: 600; color: #0f172a;
      line-height: 1.25; word-break: break-word;
    }
    .emp-mini-designation {
      font-size: 8.5px; color: #64748b;
      margin-top: 1px; line-height: 1.25;
      word-break: break-word;
    }
    .emp-mini-count {
      flex-shrink: 0; display: inline-block; white-space: nowrap;
      height: 15px; line-height: 15px;
      padding: 0 6px;
      font-size: 7.5px; font-weight: 600;
      color: #475569; background: #f1f5f9;
      border-radius: 999px;
    }
    .emp-mini-more {
      justify-content: center;
      border-style: dashed; border-left-style: dashed; border-color: #cbd5e1;
      color: #94a3b8; font-size: 8.5px; font-weight: 600;
    }

    /* Consolidated rollup node (replaces report cards below the summary depth).
       Dashed border signals "collapsed / rolled up"; navy count matches the
       staff identity colour for a premium, on-palette look. */
    .emp-summary {
      width: 200px; background: #f8fafc;
      border: 1px dashed #cbd5e1; border-radius: 10px;
      padding: 18px 12px; text-align: center;
    }
    .emp-summary-count {
      font-size: 28px; font-weight: 700; color: #1e3a8a; line-height: 1;
    }
    .emp-summary-label {
      font-size: 9px; color: #475569; font-weight: 600;
      margin-top: 7px; text-transform: uppercase; letter-spacing: 0.6px;
      line-height: 1;
    }
    .emp-summary-sub {
      font-size: 9px; color: #94a3b8; margin-top: 5px; line-height: 1;
    }
  `;

  // Source width of a single .emp-card (must match ORG_CHART_TREE_CSS).
  const ORG_CARD_SRC_PX = 200;
  // Conservative browser canvas caps. Exceeding either makes html2canvas
  // silently return a BLANK canvas — the original "empty PDF" bug for large
  // charts (e.g. All Departments). Chrome tops out ~16384px / ~268M px²;
  // Safari is lower, so we stay well under.
  const MAX_CANVAS_DIM = 16384;
  const MAX_CANVAS_AREA = 226_000_000;
  // On-paper card sizing. If fitting the whole chart onto one page would shrink
  // cards below MIN, we switch to a multi-page poster rendered at TARGET size.
  const ORG_TARGET_CARD_MM = 26;
  const ORG_MIN_CARD_MM = 16;
  // Ceiling on poster pages; beyond this we shrink cards to cap the page count.
  const ORG_MAX_TILES = 40;
  // PDF exports render a compact "top 4 levels" structure: full cards for
  // levels 1-2, level 3 as a vertical stack of compact rows (ORG_PDF_STACK_DEPTH,
  // see buildOrgChartTreeHTML's stackDepth option) with rolled-up level-4+
  // headcount per row, and — as a safety net for any depth this doesn't
  // already cover — a consolidated "N team members" node at/beyond this depth.
  // The on-screen chart is unaffected (stays fully expandable).
  const ORG_PDF_SUMMARY_DEPTH = 4;
  // Level whose members render as compact stacked rows instead of full cards.
  const ORG_PDF_STACK_DEPTH = 3;

  const exportOrgChartToPDF = async () => {
    if (selectedDepartmentEmployees.length === 0) return;

    const toastId = toast.loading('Generating organization chart PDF…');
    let container;
    try {
    const [html2canvasModule, pdfTemplate] = await Promise.all([
      import('html2canvas'),
      import('../utils/pdfReportTemplate'),
    ]);
    const html2canvas = html2canvasModule.default;
    const {
      createBrandedPDF, drawBrandedHeader, addBrandedFooter, drawMetadataBlock,
      drawSeparatorLine, sanitizeForFilename,
    } = pdfTemplate;

    // 1. Build tree HTML in an offscreen container so html2canvas can capture it
    container = document.createElement('div');
    container.style.cssText = 'position:absolute;left:-99999px;top:0;z-index:-1;';

    const styleEl = document.createElement('style');
    styleEl.textContent = ORG_CHART_TREE_CSS;
    container.appendChild(styleEl);

    const treeEl = document.createElement('div');
    treeEl.className = 'org-tree';
    treeEl.innerHTML = buildOrgChartTreeHTML(selectedDepartmentEmployees, {
      summaryDepth: ORG_PDF_SUMMARY_DEPTH,
      stackDepth: ORG_PDF_STACK_DEPTH,
    });
    container.appendChild(treeEl);
    document.body.appendChild(container);

    // Let the browser lay out the tree at its natural (possibly very wide) size
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    // 2. Measure the natural size and pick the highest capture scale that keeps
    //    the resulting canvas within browser limits. 1.5× is our target for
    //    crisp output; huge charts get clamped down so the canvas never blanks.
    const rect = treeEl.getBoundingClientRect();
    const naturalW = Math.max(1, Math.ceil(rect.width));
    const naturalH = Math.max(1, Math.ceil(rect.height));

    let captureScale = Math.min(
      1.5,
      MAX_CANVAS_DIM / naturalW,
      MAX_CANVAS_DIM / naturalH,
      Math.sqrt(MAX_CANVAS_AREA / (naturalW * naturalH))
    );
    captureScale = Math.max(captureScale, 0.05);

    const canvas = await html2canvas(treeEl, {
      scale: captureScale,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
      width: naturalW,
      height: naturalH,
      windowWidth: naturalW,
      windowHeight: naturalH,
    });

    document.body.removeChild(container);
    container = null;

    // 3. Create branded PDF (A4 landscape) using the shared template
    const scopeLabel = selectedDepartmentName === 'All' ? 'All Departments' : selectedDepartmentName;
    const groupLabel = selectedOrgGroup === 'staff'
      ? 'Staff'
      : selectedOrgGroup === 'workers'
        ? 'Workers'
        : 'All Classifications';
    const subtitle = `${scopeLabel} \u2022 ${groupLabel} \u2022 ${selectedDepartmentEmployees.length} employees \u2022 Top ${ORG_PDF_SUMMARY_DEPTH} levels`;

    const { doc, headerEndY, leftX, rightX, pageW, pageH, contentWidth } =
      await createBrandedPDF({
        title: 'Organization Chart',
        subtitle,
        orientation: 'landscape',
      });

    // Card width on paper if we shrink the whole chart onto a single page.
    const capturedCardPx = ORG_CARD_SRC_PX * captureScale;
    const fitMetaHeight = 2 * 5 + 4; // ≈ two-row metadata block
    const fitAvailW = contentWidth;
    const fitAvailH = pageH - (headerEndY + fitMetaHeight) - 12;
    const imgAspect = canvas.width / canvas.height;
    let fitW = fitAvailW;
    let fitH = fitW / imgAspect;
    if (fitH > fitAvailH) {
      fitH = fitAvailH;
      fitW = fitH * imgAspect;
    }
    const cardMmIfFit = capturedCardPx * (fitW / canvas.width);

    const filename = `Organization_Chart_${sanitizeForFilename(scopeLabel)}_${sanitizeForFilename(groupLabel)}_${new Date().toISOString().slice(0, 10)}.pdf`;

    if (cardMmIfFit >= ORG_MIN_CARD_MM) {
      // 4a. SINGLE PAGE — small chart. Keep the branded metadata block + a
      //     shrink-to-fit image centred on the page (original behaviour).
      const metaLeft = [
        ['Scope', scopeLabel],
        ['Group', groupLabel],
      ];
      const metaRight = [
        ['Employees', String(selectedDepartmentEmployees.length)],
        ['View', `Top ${ORG_PDF_SUMMARY_DEPTH} levels (L3 stacked)`],
      ];
      const metaEndY = drawMetadataBlock(doc, metaLeft, metaRight, { headerEndY, leftX, rightX, lineH: 5 });
      drawSeparatorLine(doc, metaEndY, leftX, rightX);

      const availableWidth = contentWidth;
      const availableHeight = pageH - metaEndY - 12;
      let imgW = availableWidth;
      let imgH = imgW / imgAspect;
      if (imgH > availableHeight) {
        imgH = availableHeight;
        imgW = imgH * imgAspect;
      }
      const imgX = leftX + (availableWidth - imgW) / 2;
      // JPEG @0.95 ≈ PNG here (text/cards/lines) but 15-30× smaller.
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      doc.addImage(imgData, 'JPEG', imgX, metaEndY, imgW, imgH, undefined, 'FAST');
    } else {
      // 4b. MULTI-PAGE POSTER — large chart (e.g. All Departments). Render at a
      //     legible fixed card size and tile the canvas across pages so nothing
      //     is unreadable (and, critically, nothing is blank).
      const tileTop = headerEndY + 4; // room for the section label
      const pageContentW = contentWidth;
      const pageContentH = pageH - tileTop - 12;

      // px-per-mm that yields legible cards; clamp so we never exceed ORG_MAX_TILES.
      let ppm = capturedCardPx / ORG_TARGET_CARD_MM;
      const gridFor = (p) => {
        const tpw = pageContentW * p;
        const tph = pageContentH * p;
        return {
          cols: Math.max(1, Math.ceil(canvas.width / tpw)),
          rows: Math.max(1, Math.ceil(canvas.height / tph)),
          tpw,
          tph,
        };
      };
      let grid = gridFor(ppm);
      if (grid.cols * grid.rows > ORG_MAX_TILES) {
        ppm *= Math.sqrt(ORG_MAX_TILES / (grid.cols * grid.rows));
        grid = gridFor(ppm);
      }
      const { cols, rows, tpw: tilePxW, tph: tilePxH } = grid;
      const totalTiles = cols * rows;

      const tileCanvas = document.createElement('canvas');
      const tileCtx = tileCanvas.getContext('2d');

      let tileIndex = 0;
      // Row-major = read left→right, top→bottom, matching the on-screen chart.
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const sx = c * tilePxW;
          const sy = r * tilePxH;
          const sw = Math.min(tilePxW, canvas.width - sx);
          const sh = Math.min(tilePxH, canvas.height - sy);
          if (sw <= 0 || sh <= 0) continue;

          if (tileIndex > 0) {
            doc.addPage();
            // eslint-disable-next-line no-await-in-loop
            await drawBrandedHeader(doc, { title: 'Organization Chart', subtitle });
          }
          tileIndex += 1;

          // Section locator so a printed poster can be reassembled.
          doc.setFontSize(7.5);
          doc.setTextColor(120);
          doc.text(
            `Section ${tileIndex} of ${totalTiles}  \u2022  Row ${r + 1}/${rows}, Col ${c + 1}/${cols}`,
            leftX,
            tileTop - 1
          );
          doc.setTextColor(0);

          tileCanvas.width = Math.ceil(sw);
          tileCanvas.height = Math.ceil(sh);
          tileCtx.fillStyle = '#ffffff';
          tileCtx.fillRect(0, 0, tileCanvas.width, tileCanvas.height);
          tileCtx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);

          const imgData = tileCanvas.toDataURL('image/jpeg', 0.92);
          doc.addImage(imgData, 'JPEG', leftX, tileTop, sw / ppm, sh / ppm, undefined, 'FAST');
        }
      }
    }

    // 5. Branded footer on every page, then direct download.
    addBrandedFooter(doc, { leftX, rightX });
    doc.save(filename);
    toast.success('Organization chart downloaded', { id: toastId });
    } catch (error) {
      console.error('Org chart PDF export failed:', error);
      toast.error('Failed to generate organization chart PDF', { id: toastId });
    } finally {
      if (container?.parentNode) {
        container.parentNode.removeChild(container);
      }
    }
  };

  const getEmployeePhoto = (employee) => {
    const bgHex = getClassificationAvatarHex(employee.employment_classification).replace('#', '');
    const initial = getAvatarInitial(employee);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initial)}&background=${bgHex}&color=ffffff&size=128&shape=circle`;
  };

  // Helper: classification group matcher for org chart filtering
  // 'staff' group = all non-worker classifications (staff, directors, advisors, interns, etc.)
  // 'workers' group = worker classification only
  const matchesOrgGroup = (emp) => {
    if (selectedOrgGroup === 'all') return true;
    if (selectedOrgGroup === 'workers') return emp.employment_classification === 'worker';
    if (selectedOrgGroup === 'staff') return emp.employment_classification !== 'worker';
    return true;
  };

  // --- Organization Chart data for selected department ---
  const selectedDepartmentEmployees = useMemo(() => {
    if (!employees.length) return [];

    // Special case: "All" view shows complete company hierarchy
    if (!selectedDepartment) {
      return employees.filter(emp => 
        !['inactive'].includes(emp.employment_status) &&
        matchesOrgGroup(emp)
      );
    }

    // Get all employees in the selected department
    const deptEmployees = employees.filter((emp) => (
      normalizeDepartmentId(emp?.department?.id) === selectedDepartment &&
      !['inactive'].includes(emp.employment_status)
    ));

    // Find the department's HOD(s) and Deputy HOD(s) from the departments data
    const selectedDept = departments.find(
      (dept) => normalizeDepartmentId(dept.id) === selectedDepartment
    );
    const hodIds = selectedDept?.head_of_department || [];
    const deputyHodIds = selectedDept?.deputy_head_of_department || [];
    
    // Get HODs and Deputy HODs who might not be in the department employees list
    const hods = employees.filter(emp => 
      hodIds.includes(emp.staff_id) && 
      !['inactive'].includes(emp.employment_status)
    );
    
    const deputyHods = employees.filter(emp => 
      deputyHodIds.includes(emp.staff_id) && 
      !['inactive'].includes(emp.employment_status)
    );

    // Combine department employees, HODs, and Deputy HODs, removing duplicates
    const allRelevantEmployees = new Map();
    
    deptEmployees.forEach(emp => allRelevantEmployees.set(emp.staff_id, emp));
    hods.forEach(hod => allRelevantEmployees.set(hod.staff_id, hod));
    deputyHods.forEach(deputyHod => allRelevantEmployees.set(deputyHod.staff_id, deputyHod));
    
    // Find employees who report to anyone in our current set
    const findReportsTo = (managerId) => {
      return employees.filter(emp => 
        emp.reporting_officer?.staff_id === managerId &&
        !['inactive'].includes(emp.employment_status)
      );
    };

    const currentEmployeeIds = Array.from(allRelevantEmployees.keys());
    currentEmployeeIds.forEach(empId => {
      const reports = findReportsTo(empId);
      reports.forEach(emp => {
        if (normalizeDepartmentId(emp.department?.id) === selectedDepartment ||
            hodIds.includes(emp.staff_id) || 
            deputyHodIds.includes(emp.staff_id)) {
          allRelevantEmployees.set(emp.staff_id, emp);
        }
      });
    });

    // Apply classification group filter (Staff vs Workers)
    return Array.from(allRelevantEmployees.values()).filter(matchesOrgGroup);
  }, [employees, selectedDepartment, departments, selectedOrgGroup]);

  // Get unique departments that have employees for org chart tabs
  const availableOrgDepartments = useMemo(() => {
    const departmentById = new Map();
    employees
      .filter(emp => 
        emp?.department?.id &&
        emp?.department?.department_name && 
        !['inactive'].includes(emp.employment_status)
      )
      .forEach((emp) => {
        const id = normalizeDepartmentId(emp.department.id);
        departmentById.set(id, {
          id,
          department_name: emp.department.department_name,
        });
      });

    const activeDepartments = Array.from(departmentById.values()).sort(
      (a, b) => a.department_name.localeCompare(b.department_name)
    );
    return [{ id: '', department_name: 'All' }, ...activeDepartments];
  }, [employees]);

  const activeFilterCount = (searchTerm ? 1 : 0) + (selectedDepartment ? 1 : 0);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-36 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
              </div>
            </div>
            <div className="h-6 bg-gray-200 rounded-full w-48 animate-pulse"></div>
          </div>
        </div>

        {/* Search and Filters Skeleton */}
        <div className="bg-white rounded-2xl shadow-sm border p-4">
          <div className="flex flex-col sm:flex-row gap-4 animate-pulse">
            <div className="h-10 bg-gray-200 rounded flex-1"></div>
            <div className="h-10 bg-gray-200 rounded w-48"></div>
            <div className="h-10 bg-gray-200 rounded w-10"></div>
          </div>
        </div>

        {/* Staff Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border p-4">
              <div className="flex items-center gap-4 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                  <div className="h-3 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-8">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Users className="w-16 h-16 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading staff directory</h3>
          <p className="text-gray-500 mb-4">There was a problem loading the staff data.</p>
          <button
            onClick={() => refetch()}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — Compact Page Header (employee) */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-blue-800" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Directory</h1>
        </div>
        <p className="text-sm text-gray-500 ml-12">Find colleagues and explore organizational structure</p>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-content space-y-6">
          <div className="flex items-center justify-between gap-3">
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search by name, email, staff ID, or designation..."
              theme="employee"
              className="flex-1 max-w-xl"
            />

            <div className="flex items-center gap-2">
              <FilterButton
                theme="employee"
                isOpen={showFilters}
                activeCount={selectedDepartment ? 1 : 0}
                onClick={() => setShowFilters(!showFilters)}
              />

              <PillToggle
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { id: 'grid', label: 'Grid View', icon: Grid3X3, title: 'Grid View' },
                  { id: 'list', label: 'List View', icon: List, title: 'List View' },
                  { id: 'org', label: 'Organization Chart', icon: Network, title: 'Organization Chart' },
                ]}
                variant="icon"
                theme="employee"
                ariaLabel="View mode selection"
              />
            </div>
          </div>

          {showFilters && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-wrap items-center gap-3">
                <div className="form-group">
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="select"
                  >
                    <option value="">All Departments</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.department_name}
                      </option>
                    ))}
                  </select>
                </div>

                {(searchTerm || selectedDepartment) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedDepartment('');
                    }}
                    className="btn btn-outline btn-sm self-start sm:self-auto"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {showFilters && activeFilterCount > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {searchTerm && (
                <span className="pill pill-secondary">
                  Search: {searchTerm}
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-2 text-gray-500 hover:text-gray-700"
                    title="Clear search"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedDepartment && (
                <span className="pill pill-secondary">
                  Department: {departments.find(d => d.id === selectedDepartment)?.department_name || selectedDepartment}
                  <button
                    onClick={() => setSelectedDepartment('')}
                    className="ml-2 text-gray-500 hover:text-gray-700"
                    title="Clear department filter"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {viewMode === 'grid' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {paginatedEmployees.map((employee) => (
            <div
              key={employee.staff_id}
              role="button"
              tabIndex={0}
              onClick={() => openEmployeeModal(employee)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openEmployeeModal(employee);
                }
              }}
              className="card card-hover cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <div className="card-content">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="relative">
                    <img
                      src={getEmployeePhoto(employee)}
                      alt={`${employee.first_name} ${employee.last_name}`}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="absolute -bottom-1 -right-1">
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ring-1 ring-inset ${getClassificationColor(employee.employment_classification)}`}>
                        <UserCheck className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {getDisplayName(employee)}
                    </h3>
                    {(employee.alias && employee.alias.trim()) && (
                      <p className="text-xs text-gray-500 truncate">{getLegalName(employee)}</p>
                    )}
                    <p className="text-sm text-gray-600 truncate">{employee.designation}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <ClassificationBadge classification={employee.employment_classification} />
                      <span className="pill pill-sm bg-gray-100 text-gray-600">
                        {employee.staff_id}
                      </span>
                      <RoleBadge employee={employee} size="sm" />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    {(() => { const DeptIcon = getDepartmentIcon(employee.department?.department_name); const dc = getDepartmentColor(employee.department?.department_name); return <DeptIcon className={`w-4 h-4 mr-3 ${dc.text}`} />; })()}
                    <span className="truncate">{employee.department?.department_name || 'Unassigned'}</span>
                    {employee.employment_classification === 'worker' &&
                      employee.trade_category &&
                      !isTradeRedundantWithDepartment(
                        employee.department?.department_name,
                        null,
                        null,
                        employee.trade_category,
                      ) && (
                      <span className="ml-2">
                        <TradeBadge trade={employee.trade_category} size="xs" />
                      </span>
                    )}
                  </div>
                  {getSafeWorkEmail(employee) && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="w-4 h-4 mr-3 text-gray-400" />
                      <span className="truncate">{getSafeWorkEmail(employee)}</span>
                    </div>
                  )}
                  <DirectoryPhoneLines employee={employee} />
                </div>
              </div>
            </div>
          ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalCount={filteredEmployees.length}
            pageSize={pageSize}
            theme="employee"
            noun="employees"
            className="mt-6"
          />
        </>
      )}
      
      {/* Organization Chart View */}
      {viewMode === 'org' && (
        <div className="card">
          <div className="card-content space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 whitespace-nowrap">Organization Chart</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                  {selectedDepartmentName === 'All' ? 'Complete Company Hierarchy' : `${selectedDepartmentName} Department`}
                  {selectedOrgGroup === 'staff' && ' • Staff only'}
                  {selectedOrgGroup === 'workers' && ' • Workers only'}
                </p>
              </div>
              <div className="flex items-center gap-2 self-start sm:self-auto sm:justify-end flex-shrink-0">
                <PillToggle
                  value={selectedOrgGroup}
                  onChange={setSelectedOrgGroup}
                  options={[
                    { id: 'all', label: 'All' },
                    { id: 'staff', label: 'Staff' },
                    { id: 'workers', label: 'Workers' },
                  ]}
                  variant="text"
                  theme="employee"
                  ariaLabel="Filter org chart by employee classification"
                />
                <button
                  onClick={exportOrgChartToPDF}
                  className="icon-button"
                  title="Download as PDF"
                  aria-label="Download organization chart as PDF"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>

            {availableOrgDepartments.length > 1 && (
              <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
                {availableOrgDepartments.map((department) => (
                  <button
                    key={department.id || 'all'}
                    onClick={() => setSelectedDepartment(department.id)}
                    className={`tab-btn ${selectedDepartment === department.id ? 'active' : ''}`}
                  >
                    {department.department_name}
                  </button>
                ))}
              </div>
            )}

            {selectedDepartmentEmployees.length === 0 ? (
              <div className="text-gray-500">
                {(() => {
                  const groupSuffix = selectedOrgGroup === 'staff' ? ' for the Staff group' : selectedOrgGroup === 'workers' ? ' for the Workers group' : '';
                  return selectedDepartmentName === 'All'
                    ? `No active employees found${groupSuffix}.`
                    : `No employees found in ${selectedDepartmentName} department${groupSuffix}.`;
                })()}
              </div>
            ) : (
              <Suspense fallback={
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              }>
                <OrgChart
                  employees={selectedDepartmentEmployees}
                  onEmployeeClick={openEmployeeModal}
                  getEmployeePhoto={getEmployeePhoto}
                  getDisplayName={getDisplayName}
                  searchTerm={searchTerm}
                />
              </Suspense>
            )}
          </div>
        </div>
      )}

      {viewMode === 'list' && (
        <>
          <div className="card">
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      <User className="w-4 h-4 inline mr-2" />
                      Employee
                    </th>
                    <th className="table-header-cell">
                      <Building2 className="w-4 h-4 inline mr-2" />
                      Department
                    </th>
                    <th className="table-header-cell">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Contact
                    </th>
                    <th className="table-header-cell">
                      <Briefcase className="w-4 h-4 inline mr-2" />
                      Classification
                    </th>
                    <th className="table-header-cell">
                      <UserCheck className="w-4 h-4 inline mr-2" />
                      Reporting Officer
                    </th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {paginatedEmployees.map((employee) => (
                  <tr
                    key={employee.staff_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openEmployeeModal(employee)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openEmployeeModal(employee);
                      }
                    }}
                    className="table-row cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset"
                  >
                    <td className="table-cell">
                      <div className="flex items-center">
                        <div className="relative">
                          <img
                            src={getEmployeePhoto(employee)}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div className="absolute -bottom-1 -right-1">
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ring-1 ring-inset ${getClassificationColor(employee.employment_classification)}`}>
                              <UserCheck className="w-2 h-2" />
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {getDisplayName(employee)}
                          </div>
                          {(employee.alias && employee.alias.trim()) && (
                            <div className="text-xs text-gray-500">{getLegalName(employee)}</div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm text-gray-500">{employee.designation}</span>
                            <RoleBadge employee={employee} size="sm" />
                          </div>
                          <div className="pill pill-sm bg-gray-100 text-gray-600 mt-1">
                            {employee.staff_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        {(() => { const DeptIcon = getDepartmentIcon(employee.department?.department_name); const dc = getDepartmentColor(employee.department?.department_name); return <DeptIcon className={`w-4 h-4 mr-2 ${dc.text}`} />; })()}
                        {employee.department?.department_name || 'Unassigned'}
                      </div>
                      {employee.employment_classification === 'worker' &&
                        employee.trade_category &&
                        !isTradeRedundantWithDepartment(
                          employee.department?.department_name,
                          null,
                          null,
                          employee.trade_category,
                        ) && (
                        <div className="mt-1">
                          <TradeBadge trade={employee.trade_category} size="xs" />
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="space-y-1">
                        {getSafeWorkEmail(employee) && (
                          <div className="flex items-center text-sm">
                            <Mail className="w-3 h-3 mr-2 text-gray-400" />
                            {getSafeWorkEmail(employee)}
                          </div>
                        )}
                        <DirectoryPhoneLines employee={employee} dense />
                      </div>
                    </td>
                    <td className="table-cell">
                      <ClassificationBadge classification={employee.employment_classification} />
                    </td>
                    <td className="table-cell">
                      {employee.reporting_officer ? (
                        <div className="flex items-center">
                          <UserCheck className="w-4 h-4 mr-2 text-gray-400" />
                          {employee.reporting_officer.first_name} {employee.reporting_officer.last_name}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">None</span>
                      )}
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalCount={filteredEmployees.length}
            pageSize={pageSize}
            theme="employee"
            noun="employees"
            className="mt-6"
          />
        </>
      )}

      {/* Empty State */}
      {viewMode !== 'org' && paginatedEmployees.length === 0 && filteredEmployees.length === 0 && !isLoading && (
        <div className="card">
          <div className="card-content text-center py-12">
            <div className="dashboard-card-icon mx-auto mb-4">
              <Users className="w-8 h-8" />
            </div>
            <h3 className="dashboard-card-title">No staff found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedDepartment
                ? 'Try adjusting your search criteria or filters.'
                : 'No active staff members in the directory.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {(searchTerm || selectedDepartment) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedDepartment('');
                  }}
                  className="btn btn-outline self-start sm:self-auto"
                >
                  <X className="w-4 h-4" />
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Employee Detail Modal */}
      {showEmployeeModal && selectedEmployee && (
        <Modal
          isOpen
          onClose={closeEmployeeModal}
          size="md"
          theme="employee"
          customHeader={(
            <div className="modal-header">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-blue-600" />
                <h2 className="modal-title">Staff Details</h2>
              </div>
              <button
                onClick={closeEmployeeModal}
                className="modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        >
                <div className="flex items-start gap-3 sm:gap-6 mb-6 sm:mb-8 min-w-0">
                  <div className="relative flex-shrink-0">
                    <img
                      src={getEmployeePhoto(selectedEmployee)}
                      alt=""
                      className="w-16 h-16 sm:w-24 sm:h-24 rounded-full object-cover"
                    />
                    <div className="absolute -bottom-2 -right-2">
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ring-1 ring-inset ${getClassificationColor(selectedEmployee.employment_classification)}`}>
                        <UserCheck className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-2xl font-bold text-gray-900 truncate">
                      {getDisplayName(selectedEmployee)}
                    </h3>
                    {(selectedEmployee.alias && selectedEmployee.alias.trim()) && (
                      <div className="text-xs sm:text-sm text-gray-500 truncate">{getLegalName(selectedEmployee)}</div>
                    )}
                    <p className="text-sm sm:text-lg text-gray-600 mt-1 truncate">{selectedEmployee.designation}</p>
                    <div className="flex items-center gap-1.5 sm:gap-2 mt-2 sm:mt-3 flex-wrap">
                      <ClassificationBadge classification={selectedEmployee.employment_classification} />
                      <span className="pill pill-secondary">
                        {selectedEmployee.staff_id}
                      </span>
                      <RoleBadge employee={selectedEmployee} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center mb-4">
                      <Mail className="w-5 h-5 mr-2 text-blue-600" />
                      <h4 className="text-base font-semibold text-gray-900">Contact Information</h4>
                    </div>
                    <DirectoryContactDetails employee={selectedEmployee} />
                  </div>

                  <div>
                    <div className="flex items-center mb-4">
                      <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                      <h4 className="text-base font-semibold text-gray-900">Organization</h4>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        {(() => { const DeptIcon = getDepartmentIcon(selectedEmployee.department?.department_name); const dc = getDepartmentColor(selectedEmployee.department?.department_name); return <DeptIcon className={`w-4 h-4 mr-3 ${dc.text}`} />; })()}
                        <span className="text-gray-700">{selectedEmployee.department?.department_name || 'Unassigned'}</span>
                        {selectedEmployee.employment_classification === 'worker' &&
                          selectedEmployee.trade_category &&
                          !isTradeRedundantWithDepartment(
                            selectedEmployee.department?.department_name,
                            null,
                            null,
                            selectedEmployee.trade_category,
                          ) && (
                          <span className="ml-2">
                            <TradeBadge trade={selectedEmployee.trade_category} size="xs" />
                          </span>
                        )}
                      </div>
                      {selectedEmployee.reporting_officer && (
                        <div className="flex items-center">
                          <UserCheck className="w-4 h-4 mr-3 text-gray-400" />
                          <span className="text-gray-700">
                            Reports to: {(selectedEmployee.reporting_officer.alias && selectedEmployee.reporting_officer.alias.trim())
                              ? `${selectedEmployee.reporting_officer.alias.trim()} (${getLegalName(selectedEmployee.reporting_officer)})`
                              : `${selectedEmployee.reporting_officer.first_name} ${selectedEmployee.reporting_officer.last_name}`}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <Briefcase className="w-4 h-4 mr-3 text-gray-400" />
                        <span className="text-gray-700">
                          Started: {selectedEmployee.employment_start_date ? 
                            formatDate(selectedEmployee.employment_start_date) : 
                            'Not specified'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
        </Modal>
      )}
    </div>
  );
};

export default StaffDirectory;
